import { produce } from 'immer';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { Link } from 'react-router-dom';
import { customEvents } from '../../../../../../common/constants';
import {
  formatDate,
  formatPrice,
  showErrorToast,
} from '../../../../../../common/global/helpers';
import useMount from '../../../../../../common/hooks/useMount';
import Breadcrumbs from '../../../../../../common/widgets/Breadcrumbs';
import Button from '../../../../../../common/widgets/Button';
import Icons from '../../../../../../common/widgets/Icons';
import Loader from '../../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../../common/widgets/LoadingSpinner';
import PopoverDialog from '../../../../../../common/widgets/PopoverDialog';
import { CustomerInfoContext } from '../../../../contexts/CustomerInfoContext';
import { TagType } from '../../../../enums';
import { rewriteImageUrl } from '../../../../helpers';
import {
  OrderDetailsModel,
  OrderProductDisplayModel,
} from '../../../../models/Orders';
import {
  CanCancelSubscriptionTypes,
  SubscriptionItemModel,
} from '../../../../models/Subscriptions';
import { resources } from '../../../../resources/resources';
import { orderService } from '../../../../services/orders';
import applicationCrumbs from '../../../ApplicationCrumbs';
import CancelSubscriptionDialog from '../../../Common/CancelSubscriptionDialog';
import PlatformInformation from '../../../Common/PlatformInformation';
import MyAccountMenu from '../../Common/MyAccountMenu';
interface OrderDetailsPageState {
  orderDetails?: OrderDetailsModel;
  isInvoiceButtonLoading: boolean;
  showErrorPanel?: boolean;
}

export const OrderDetailsPage = () => {
  const { t: translate } = useTranslation();
  const crumbs = applicationCrumbs(translate);
  const navigate = useNavigate();
  const [state, setState] = useState<OrderDetailsPageState>({
    isInvoiceButtonLoading: false,
  });
  const [selectedOrderProduct, setSelectedOrderProduct] =
    useState<OrderProductDisplayModel | null>(null);
  const { customerInfo } = useContext(CustomerInfoContext);
  const { orderId } = useParams();

  const loadOrderDetails = async () => {
    if (!orderId) return;

    // retry strategy for 404 Not Found: immediate, then after 5s, 10s, 20s
    const delays = [0, 5000, 10000, 20000];

    const is404 = (resp: any) => {
      return resp?.errorCode === 'errors.notfound';
    };

    for (const delay of delays) {
      if (delay > 0) {
        await new Promise(r => setTimeout(r, delay));
      }

      try {
        const resp = await orderService.getOrderDetails(
          Number.parseInt(orderId),
        );

        if (!resp.hasErrors) {
          document.title = `${translate(resources.menu.orders)}: ${orderId}`;
          setState({
            orderDetails: {
              ...resp.data,
              dateAdded: formatDate(resp.data.dateAdded),
            },
            isInvoiceButtonLoading: false,
          });
          return;
        }

        if (!is404(resp)) {
          setState(
            produce(d => {
              d.showErrorPanel = true;
            }),
          );
          return;
        }
      } catch (err: any) {
        if (!is404(err)) {
          setState(
            produce(d => {
              d.showErrorPanel = true;
            }),
          );
          return;
        }
      }
    }

    setState(
      produce(d => {
        d.showErrorPanel = true;
      }),
    );
  };

  const downloadInvoice = async () => {
    if (!state.orderDetails?.orderId) return;

    setState(
      produce(d => {
        d.isInvoiceButtonLoading = true;
      }),
    );

    const resp = await orderService.downloadOrderInvoice(
      state.orderDetails.orderId,
      translate(resources.orders.invoice, {
        0: `#${state.orderDetails.orderId}`,
      }),
    );

    setState(
      produce(d => {
        d.isInvoiceButtonLoading = false;
      }),
    );

    if (resp.hasErrors) {
      showErrorToast(translate(resources.errors.cannotGenerateFile));
    }
  };

  const handleCancelSubscriptionClicked = (
    orderProduct: OrderProductDisplayModel,
  ) => {
    setSelectedOrderProduct(orderProduct);
  };

  useMount(() => {
    loadOrderDetails();
  });

  return (
    <>
      <Breadcrumbs
        entries={[crumbs.home, crumbs.accountDashboard, crumbs.orders]}
      />
      <section className="section-main section-actions my-account-page">
        <div className="container">
          <div className="area-actions">
            <MyAccountMenu />
          </div>
          <div className="area-content">
            <PlatformInformation />
            <div className="panel padding-xl">
              <div className="page-actions">
                <a onClick={() => navigate(-1)} className="btn btn-panel-back">
                  <span className="btn-text">
                    {translate(resources.orders.title)}
                  </span>
                </a>

                {!!state.orderDetails &&
                  state.orderDetails.hasInvoice &&
                  !state.showErrorPanel && (
                    <div className="page-action">
                      <Button
                        className="btn btn-solid btn-sm"
                        loading={state.isInvoiceButtonLoading}
                        onClick={downloadInvoice}
                        icon={<Icons.DownloadFile />}
                      >
                        <span className="btn-text">
                          {translate(resources.orders.downloadInvoice)}
                        </span>
                      </Button>
                    </div>
                  )}
              </div>
              <Loader
                loading={!state.orderDetails}
                loader={<LoadingSpinner />}
                showErrorPanel={state.showErrorPanel}
              >
                {!!state.orderDetails && (
                  <>
                    <div className="order-details-panel">
                      <ul>
                        <li>
                          <span>{translate(resources.orders.orderId)}:</span>{' '}
                          <span>#{state.orderDetails.orderId}</span>
                        </li>
                        <li>
                          <span>{translate(resources.orders.orderDate)}:</span>{' '}
                          <span>{state.orderDetails.dateAdded}</span>
                        </li>
                        <li>
                          <span>
                            {translate(resources.orders.orderStatus)}:
                          </span>{' '}
                          <span>{state.orderDetails.statusName}</span>
                        </li>
                        <li>
                          <span>
                            {translate(resources.orders.paymentMethod)}:
                          </span>{' '}
                          <span>{state.orderDetails.paymentMethod}</span>
                        </li>
                      </ul>
                      <ul>
                        <li>
                          <span className="order-details-total-cell">
                            {translate(resources.orders.total)}:
                          </span>{' '}
                          <span className="order-details-total-cell order-details-sum">
                            {formatPrice(state.orderDetails.total)}{' '}
                            {state.orderDetails.currencyCode}
                          </span>
                        </li>
                        {customerInfo.platformType === 1 && (
                          <li>
                            <span>
                              {translate(resources.orders.paidFromBudget)}:
                            </span>{' '}
                            <span className="order-details-sum">
                              {formatPrice(state.orderDetails.paidFromBudget)}{' '}
                              {state.orderDetails.currencyCode}
                            </span>
                          </li>
                        )}
                        {customerInfo.platformType === 1 && (
                          <li>
                            <span>
                              {translate(resources.orders.personalContribution)}
                              :
                            </span>{' '}
                            <span className="order-details-sum">
                              {formatPrice(
                                state.orderDetails.personalContribution,
                              )}{' '}
                              {state.orderDetails.currencyCode}
                            </span>
                          </li>
                        )}
                        {customerInfo.platformType === 1 && (
                          <li>
                            <span>{translate(resources.orders.taxes)}:</span>{' '}
                            <span className="order-details-sum">
                              {formatPrice(state.orderDetails.taxes)}{' '}
                              {state.orderDetails.currencyCode}
                              {customerInfo.isGrossUpUser && (
                                <PopoverDialog
                                  className="info-tooltip"
                                  content={<div>GROSS UP TEXT TODO</div>}
                                  appendTo={document.body}
                                  type="hover"
                                  placement="top"
                                  closeFromContent
                                >
                                  <span>
                                    <Icons.InfoSmall />
                                  </span>
                                </PopoverDialog>
                              )}
                            </span>
                          </li>
                        )}
                      </ul>
                    </div>
                    <div className="order-product-list">
                      {state.orderDetails.products.map((e, idx) => (
                        <div key={idx} className="order-product">
                          <div className="order-product-properties">
                            <div className="order-product-image">
                              <div>
                                {e.isAvailable ? (
                                  <Link
                                    to={
                                      e.isCardType
                                        ? `/my-account/cards`
                                        : `/products/${e.productId}`
                                    }
                                  >
                                    <img
                                      src={rewriteImageUrl(e.image)}
                                      alt={e.name || ''}
                                    />
                                  </Link>
                                ) : (
                                  <span>
                                    <img
                                      src={rewriteImageUrl(e.image)}
                                      alt={e.name || ''}
                                    />
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="order-product-details">
                              <h3>
                                {e.isAvailable ? (
                                  <Link
                                    to={
                                      e.isCardType
                                        ? `/my-account/cards`
                                        : `/products/${e.productId}`
                                    }
                                  >
                                    {e.name}
                                  </Link>
                                ) : (
                                  <span className="summary-label">
                                    <PopoverDialog
                                      content={
                                        <div>
                                          {translate(
                                            resources.orders
                                              .productNoLongerAvailable,
                                          )}
                                        </div>
                                      }
                                      appendTo={document.body}
                                      type="hover"
                                      placement="top"
                                      closeFromContent
                                      className="topup-tooltip"
                                    >
                                      <div>{e.name}</div>
                                    </PopoverDialog>
                                  </span>
                                )}
                              </h3>

                              {e.availability?.map(t => (
                                <div
                                  className="order-product-availability"
                                  key={t.id}
                                >
                                  <div
                                    key={t.id}
                                    className={`checkout-list-availability ${
                                      t.type === TagType.DisponibilityNowType
                                        ? 'available'
                                        : 'available-next-month'
                                    }`}
                                  >
                                    <span className="checkout-list-status">
                                      {t.description}
                                    </span>
                                    <span className="checkout-list-status-info">
                                      {t.label}
                                    </span>
                                  </div>
                                </div>
                              ))}
                              {e.startMonth !== null &&
                                e.startYear !== null && (
                                  <div className="order-product-subscription">
                                    <div
                                      className={`checkout-list-subscription ${
                                        e.subscriptionExpired
                                          ? 'inactive'
                                          : 'active'
                                      }`}
                                    >
                                      <span className="checkout-list-subscription-label">
                                        {translate(
                                          resources.orders.subscriptionPeriod,
                                        )}
                                      </span>
                                      <span className="checkout-list-subscription-status">
                                        {`${e.startMonth}/${e.startYear} - ${e.endMonth}/${e.endYear}`}
                                      </span>
                                    </div>
                                  </div>
                                )}

                              <div
                                className="order-cancel-subscription"
                                id={e.productId.toString()}
                              >
                                {e.canCancelSubscription !==
                                  CanCancelSubscriptionTypes.Hide && (
                                  <Button
                                    disabled={
                                      e.canCancelSubscription ===
                                      CanCancelSubscriptionTypes.Disable
                                    }
                                    className="btn btn-solid btn-sm"
                                    loading={e.isCanceling}
                                    onClick={() =>
                                      handleCancelSubscriptionClicked(e)
                                    }
                                    icon={<Icons.Subtract />}
                                  >
                                    <span className="btn-text">
                                      {translate(
                                        resources.admin.employeeBenefits.details
                                          .endSubscription,
                                      )}
                                    </span>
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                          <ul className="order-product-info">
                            {e.options.map(o => (
                              <li key={o.name}>
                                {o.name}:
                                <span
                                  dangerouslySetInnerHTML={{ __html: o.value }}
                                />
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </Loader>
            </div>
          </div>
        </div>
        {selectedOrderProduct && (
          <CancelSubscriptionDialog
            product={
              {
                orderProductId: selectedOrderProduct.orderProductId,
                name: selectedOrderProduct.name,
                cancelType: selectedOrderProduct.cancelType,
              } as SubscriptionItemModel
            }
            onSuccess={() => {
              loadOrderDetails();
              document.dispatchEvent(
                new CustomEvent(customEvents.refreshCustomerBalances),
              );
            }}
            onClose={() => setSelectedOrderProduct(null)}
          />
        )}
      </section>
    </>
  );
};
